// src/components/SelectableCard.jsx
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import currenyGreen from "../assets/images/currency_white_1.png";
import useSelectableCardController from "../controllers/useSelectableCardController";
import { BASE_IMAGE_URL } from "../utils/api";

import DEFAULT_IMAGE from "../assets/images/default_1.png";
import share from "../assets/images/share.png";
import Swal from "sweetalert2";
import { pushAddToCart } from "../utils/gtmAddToCart";

// ---------- local guest-cart helpers ----------
const LOCAL_CART_KEY = "cart"; // array of part IDs
function safeGetLocalCart() {
  try {
    if (typeof window === "undefined") return [];
    const raw = localStorage.getItem(LOCAL_CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function saveLocalCart(arr, analyticsItem) {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(LOCAL_CART_KEY, JSON.stringify(arr));

    // Only push when full item info is provided
    if (analyticsItem) {
      pushAddToCart(analyticsItem); // { item_name, item_id, price, quantity }
    }
  } catch { }
}

function addToLocalCart(partId, analyticsItem) {
  const cart = new Set(safeGetLocalCart().map(String));
  cart.add(String(partId));
  saveLocalCart(Array.from(cart), analyticsItem);
}

function isInLocalCart(partId) {
  // إذا في توكن في localStorage، رجّع null فورًا
  if (localStorage.getItem('token')) {
    return null;
  }

  // غير هيك، نكمّل بفحص السلة
  const cart = safeGetLocalCart();
  return cart.some((x) => String(x) === String(partId));
}


function removeFromLocalCart(partId) {
  const next = safeGetLocalCart().filter((x) => String(x) !== String(partId));
  saveLocalCart(next);
}
// ---------------------------------------------

const SelectableCard = ({
  item,
  onClick,
  isMobile = false,
  // NEW: override "part" detection from parent (e.g. Favorites)
  isPartOverride,
  // NEW: let parent react after wishlist toggles (remove from list, etc.)
  onWishlistChange,
  setCurrentView
}) => {
  const {
    // type inferred from item
    isPart,
    isCategory,
    isBrand,
    isModel,

    // basics
    id,
    label,
    price,
    market_price,
    imageSrc,

    // hover
    hovered,
    setHovered,

    // share
    shareRef,
    showShareBox,
    setShowShareBox,
    partUrl,

    // wishlist
    inWishlist,
    wishLoading,
    handleToggleWishlist, onWishlistClick,

    // cart (server)
    cartItemId,
    checkingCart,
    handleRemove,
    handleAdd,

    // guest (from controller – we'll intercept it)
    showGuestModal,
    guestPhone,
    setGuestPhone,
    guestLoading,
    guestError,
    closeGuestModal,
    confirmGuestAdd,
  } = useSelectableCardController(item, setCurrentView);

  if (!item) return null;
  // Use override if provided, otherwise fall back to controller inference
  const effectiveIsPart =
    typeof isPartOverride === "boolean" ? isPartOverride : isPart;

  const [copied, setCopied] = React.useState(false);

  // Track guest (local) cart state for this part
  const [guestInCart, setGuestInCart] = React.useState(() => isInLocalCart(id));

  // Keep guestInCart in sync if id changes or other tabs update storage
  React.useEffect(() => {
    setGuestInCart(isInLocalCart(id));
    const onStorage = (e) => {
      if (e.key === LOCAL_CART_KEY) setGuestInCart(isInLocalCart(id));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [id]);

  // Intercept guest add → store in localStorage instead of phone modal
  const interceptedRef = React.useRef(false);
  React.useEffect(() => {
    if (showGuestModal && !interceptedRef.current) {
      interceptedRef.current = true;
      const analyticsItem = {
        item_id: id,            // part id
        item_name: label,       // part name
        price: price,           // part price (number/string ok)
        quantity: 1             // this card adds one item
      };
      addToLocalCart(id, analyticsItem); // <-- pushes event via saveLocalCart
      setGuestInCart(true);
      closeGuestModal?.();
      setTimeout(() => (interceptedRef.current = false), 150);
    }
  }, [showGuestModal, id, closeGuestModal]);

  // A unified click handler for the cart button
  const onCartClick = (e) => {
    e.stopPropagation();

    // لو لسه بيتشيّك أو القطعة موجودة أصلاً -> لا تعمل شيء
    if (checkingCart || cartItemId || guestInCart) return;

    // لسه مش في السلة -> أضف
    handleAdd(e);
  };
  // derive button states/text



  const isInAnyCart = !!cartItemId || guestInCart;
  const cartBtnLabel = checkingCart
    ? "..."
    : isInAnyCart
      ? "تمت الاضافة"
      : "إضافة للسلة";

  const isAddDisabled = checkingCart || isInAnyCart;
  // ============================================================
  // PART CARD — 3-up slider skin (unchanged behavior for parts)
  // ============================================================
  if (effectiveIsPart) {
    return (
      <>
        <div
          className="part-card-box"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => onClick?.(id)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onClick?.(id);
          }}
          title={`قطعة ${label}`}
          aria-busy={checkingCart}
        >
          {/* Title */}
          <div className="part-title" dir="rtl">
            {label}
          </div>

          {/* Product image */}
          <div className="part-media">
            <img
              src={imageSrc || DEFAULT_IMAGE}
              alt={label}
              onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE)}
            />
          </div>

          {/* Price + wishlist */}
          <div className="part-meta" dir="rtl">
            <div className="price">
              <p style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {!isMobile && (
                  <span style={{ textDecoration: "line-through", color: "#aaa" }}>
                    {market_price ? `${market_price}` : "100000"}
                  </span>
                )}
                <span>
                  {price ? `${price}` : "—"}
                </span>
                <img src={currenyGreen} alt="currency" width={40} />
              </p>
            </div>

            <button
              className={`wish-btn ${inWishlist ? "active" : ""}`}
              type="button"
              aria-label={inWishlist ? "إزالة من المفضلة" : "أضِف إلى المفضلة"}
              aria-pressed={inWishlist}
              onClick={onWishlistClick}   // <-- HERE
              title={inWishlist ? "إزالة من المفضلة" : "أضِف إلى المفضلة"}
              disabled={wishLoading}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill={inWishlist ? "currentColor" : "none"} stroke="currentColor">
                <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-8.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
              </svg>
            </button>
          </div>

          {/* Actions */}
          <div className="part-actions" dir="rtl">
            <button
              className={`btn solid ${isAddDisabled ? 'is-disabled' : ''}`}
              aria-disabled={isAddDisabled}
              onClick={isAddDisabled ? undefined : (e) => onCartClick(e)}
            >
              {cartBtnLabel}
            </button>

            <div className="pd-share" ref={shareRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                className="pd-icon-btn pd-icon-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowShareBox((v) => {
                    const next = !v;
                    if (next) setCopied(false);
                    return next;
                  });
                }}
                title="مشاركة"
                style={{ background: "transparent", borderRadius: "10px" }}
              >
                <img src={share} alt="share" width={20} />
              </motion.button>

              <AnimatePresence>
                {showShareBox && (
                  <motion.div
                    className="pd-pop"
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="pd-pop-row">
                      <p className="pd-pop-link" title={partUrl}>
                        {partUrl}
                      </p>
                      <button
                        className="pd-copy"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(partUrl).then(() => {
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          });
                        }}
                      >
                        {copied ? "تم النسخ" : "نسخ"}
                      </button>
                    </div>

                    <div className="pd-soc">
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${partUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Facebook"
                      >
                        <i className="bi bi-facebook" />
                      </a>
                      <a
                        href={`https://twitter.com/intent/tweet?url=${partUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="Twitter"
                      >
                        <i className="bi bi-twitter" />
                      </a>
                      <a
                        href={`https://api.whatsapp.com/send?text=${partUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="WhatsApp"
                      >
                        <i className="bi bi-whatsapp" />
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ============================================================
  // DEFAULT CARD (brand / model / year / categories / subcats)
  // Disable + “Coming Soon” ribbon when parts_count === 0
  // ============================================================

  // Safely read count from various shapes
  const partsCount = Number(
    item?.parts_count ?? item?.partsCount ?? item?.count ?? 0
  );
  const isDisabled = Number.isFinite(partsCount) && partsCount <= 0;

  const handleCardClick = () => {
    if (isDisabled) return; // ignore clicks when disabled
    onClick?.(item.id);
  };

  return (
    <button
      type="button"
      className={`selectable-card ${isDisabled ? "is-disabled" : ""}`}
      onClick={handleCardClick}
      title={isDisabled ? "قريبًا" : `قطع غيار ${label}`}
      aria-disabled={isDisabled}
    >
      {/* Ribbon */}
      {isDisabled && <span className="ribbon">بنوفره قريبًا</span>}

      {isBrand && (
        <div className="logo-box">
          <img
            src={imageSrc || DEFAULT_IMAGE}
            alt=""
            loading="lazy"
            onError={(e) => (e.currentTarget.src = DEFAULT_IMAGE)}
          />
        </div>
      )}

      <div
        className="label"
        dir="rtl"
        style={item.year ? { fontSize: 22 } : undefined}
      >
        {label}
      </div>


    </button>
  );
};

export default SelectableCard;
